import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, allArticles } from '@/mocks/zh/article';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'latest' | 'popular'>('latest');

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const filteredArticles = useMemo(() => {
    let articles = allArticles;
    if (slug && slug !== 'all') {
      articles = allArticles.filter((a) => a.category === slug);
    }
    if (sortBy === 'latest') {
      return [...articles].sort((a, b) => b.date.localeCompare(a.date));
    }
    return [...articles].sort((a, b) => b.views - a.views);
  }, [slug, sortBy]);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 9;
  const totalPages = Math.ceil(filteredArticles.length / pageSize);
  const pagedArticles = filteredArticles.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: currentCategory ? `${currentCategory.name} - 51黑料网` : '全部分类 - 51黑料网',
    description: currentCategory?.description || '浏览51黑料网所有爆料内容',
    url: `https://www.51heiliao.com/category/${slug || 'all'}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: pagedArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://www.51heiliao.com/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-background-50 pt-20">
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

        <div className="w-full px-4 md:px-6">
          <div className="py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(currentCategory
                  ? [{ label: currentCategory.name }]
                  : [{ label: '全部分类' }]),
              ]}
            />
          </div>

          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
              {currentCategory ? currentCategory.name : '全部分类'}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-500 mt-2">{currentCategory.description}</p>
            )}
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            <Link
              to="/category/all"
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                !slug || slug === 'all'
                  ? 'bg-accent-500 text-foreground-950'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  slug === cat.slug
                    ? 'bg-accent-500 text-foreground-950'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-400">
              共找到 <strong className="text-foreground-700">{filteredArticles.length}</strong> 篇爆料
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              <button
                onClick={() => { setSortBy('latest'); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === 'latest' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500'
                }`}
              >
                最新
              </button>
              <button
                onClick={() => { setSortBy('popular'); setCurrentPage(1); }}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortBy === 'popular' ? 'bg-background-50 text-foreground-950' : 'text-foreground-500'
                }`}
              >
                最热
              </button>
            </div>
          </div>

          {filteredArticles.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">这个分类还没有爆料内容</p>
              <Link to="/category/all" className="inline-flex items-center gap-1 text-accent-600 mt-3 text-sm font-medium">
                <i className="ri-arrow-left-line"></i> 查看全部分类
              </Link>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {pagedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 bg-accent-500 text-foreground-950 text-xs font-semibold rounded-full">
                          {article.categoryName}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-semibold text-foreground-900 leading-snug line-clamp-2 mb-3 group-hover:text-accent-700 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2 mb-4">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 text-xs text-foreground-400">
                          <span><i className="ri-calendar-line mr-1"></i>{article.date}</span>
                          <span><i className="ri-eye-line mr-1"></i>
                            {article.views >= 10000 ? `${(article.views / 10000).toFixed(0)}万` : article.views}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-accent-600 whitespace-nowrap group-hover:translate-x-1 transition-transform inline-flex items-center gap-0.5">
                          阅读全文 <i className="ri-arrow-right-s-line"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 py-12">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`w-10 h-10 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                        page === currentPage
                          ? 'bg-accent-500 text-foreground-950'
                          : 'text-foreground-500 hover:bg-background-100'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="w-10 h-10 rounded-full flex items-center justify-center text-foreground-500 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}